// Locale constants shared by the i18n runtime and the [lang] route layer. Kept
// dependency-light so it can be imported anywhere. The active locale is owned
// by the `[lang]` URL segment (see the app's app/[lang]/_layout.tsx) — there's no device
// detection here.

import { SITE } from '@site';

import type { SiteConfig } from '../site-config';

export const SUPPORTED_LOCALES = SITE.locales;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = SITE.defaultLocale;

// Human-facing names for the language switcher (each in its own language).
// Keyed by every language a site may enable, not just this site's.
export const LOCALE_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  ru: 'Русский',
};

// BCP-47 tags for Intl.* formatting (es: the US variety — miami-todo.com's
// Spanish speakers are mostly Latin American). FR/DE/ES/RU pick their own locale's date, time
// and number conventions (24h clock, month names, separators) automatically.
// A site overrides a tag with SITE.intlLocales (miami: en-US).
const BCP47: Record<string, string> = {
  en: 'en-GB',
  fr: 'fr-FR',
  de: 'de-DE',
  es: 'es-US',
  ru: 'ru-RU',
  // Widened to the SiteConfig shape: intlLocales is absent from the other sites' literal types.
  ...(SITE as SiteConfig).intlLocales,
};

export function isSupportedLocale(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** BCP-47 tag for `Intl.DateTimeFormat` / `toLocaleString`, falling back safely. */
export function intlLocaleTag(locale: string | null | undefined): string {
  return isSupportedLocale(locale) ? BCP47[locale] : BCP47[DEFAULT_LOCALE];
}
